import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

/**
 * Bộ lọc hai cột, học từ ycombinator.com/companies:
 * cột trái là các nhóm ô chọn (mỗi lựa chọn có số đếm), cột phải là ô tìm,
 * dòng "Hiển thị N" và kết quả. Dùng chung cho Công cụ, Mẫu, Playbook,
 * Nguồn vốn và Bản chuẩn bị, để người dùng chỉ phải học một cách lọc.
 *
 * Logic lọc và đếm nằm ở shared/locMuc.js (hàm thuần, có test).
 * Trên màn hình hẹp, cột trái thu thành nút "Bộ lọc" đặt trên kết quả.
 */

export function KhungHaiCot({ boLoc, soDangChon = 0, children, nhanLoc = "Bộ lọc", id }) {
  const [mo, setMo] = useState(false);
  return (
    <div className="lhc" id={id}>
      <aside className={`lhc-trai${mo ? " mo" : ""}`} aria-label={nhanLoc}>
        <button type="button" className="lhc-mo" aria-expanded={mo} onClick={() => setMo(!mo)}>
          <svg aria-hidden="true" viewBox="0 0 20 14"><path d="M1 2h18M4 7h12M8 12h4" /></svg>
          {nhanLoc}
          {soDangChon > 0 && <em>{soDangChon}</em>}
          <span className="lhc-mo-mui" aria-hidden="true">{mo ? "−" : "+"}</span>
        </button>
        <div className="lhc-the">{boLoc}</div>
      </aside>
      <div className="lhc-phai">{children}</div>
    </div>
  );
}

/**
 * Một nhóm ô chọn.
 * - ds: [{ v, nhan, dem?, mau?, to?, title? }]
 * - chon: mảng giá trị đang chọn; onDoi(mảng mới).
 * - Có `to` ở từng lựa chọn: nhóm dạng liên kết (mỗi lựa chọn là một trang
 *   riêng, ví dụ chủ đề Playbook). Chọn một, và link đọc được bởi Google.
 * - tatCa: chữ của dòng "Tất cả" (bỏ trống để không hiện dòng này).
 */
export function NhomLoc({ tieuDe, ds, chon = [], onDoi, tatCa = "Tất cả", tatCaTo, tatCaDem, gioiHan = 7, moSan = true, mot = false }) {
  const [mo, setMo] = useState(moSan);
  const [het, setHet] = useState(false);
  const laLink = ds.some((x) => x.to);
  // Lựa chọn đang tick luôn hiện, kể cả khi nằm sau giới hạn.
  const hien = het || ds.length <= gioiHan + 1 ? ds : ds.filter((x, i) => i < gioiHan || chon.includes(x.v));
  const an = ds.length - hien.length;
  const doi = (v) => {
    if (mot) onDoi(chon.includes(v) ? [] : [v]);
    else onDoi(chon.includes(v) ? chon.filter((x) => x !== v) : [...chon, v]);
  };
  // Hàm vẽ một dòng (không phải component con) để nút giữ được focus sau khi bấm.
  const dong = ({ x, bat, onClick, to, key }) => {
    const noiDung = (
      <>
        <span className={`lhc-o${mot || laLink ? " tron" : ""}`} aria-hidden="true" style={x.mau ? { "--mau": x.mau } : undefined} />
        <span className="lhc-nhan">{x.nhan}</span>
        {x.dem != null && <span className="lhc-dem">{x.dem}</span>}
      </>
    );
    if (to) {
      return (
        <Link key={key} className={`lhc-dong${bat ? " bat" : ""}`} to={to} aria-current={bat ? "page" : undefined} title={x.title}>
          {noiDung}
        </Link>
      );
    }
    return (
      <button key={key} type="button" className={`lhc-dong${bat ? " bat" : ""}${x.dem === 0 && !bat ? " rong" : ""}`}
        role={mot ? "radio" : "checkbox"} aria-checked={bat} onClick={onClick} title={x.title}>
        {noiDung}
      </button>
    );
  };
  return (
    <section className="lhc-nhom">
      <h2 className="lhc-tieu-de">
        <button type="button" aria-expanded={mo} onClick={() => setMo(!mo)}>
          {tieuDe}
          <span className="lhc-thu" aria-hidden="true">{mo ? "−" : "+"}</span>
        </button>
      </h2>
      {mo && (
        <div className="lhc-ds" role={laLink ? undefined : "group"} aria-label={tieuDe}>
          {tatCa && (
            dong({ key: "__tat-ca", x: { v: "", nhan: tatCa, dem: tatCaDem }, bat: !chon.length, to: laLink ? tatCaTo : undefined, onClick: () => onDoi([]) })
          )}
          {hien.map((x) => (
            dong({ key: x.v, x, bat: chon.includes(x.v), to: x.to, onClick: () => doi(x.v) })
          ))}
          {ds.length > gioiHan + 1 && (
            <button type="button" className="lhc-xem-them" onClick={() => setHet(!het)}>
              {het ? "Thu gọn" : `Xem thêm ${an} lựa chọn`}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Ô tìm đặt trong khung trắng ở đầu cột phải, như ô Search của YC.
 *
 * Chữ đang gõ giữ ở state riêng của ô, không đọc ngược từ URL: các trang ghi
 * từ khoá lên URL (?q=), và URL cập nhật chậm hơn một nhịp. Bộ gõ tiếng Việt
 * (Telex, VNI) sửa chữ ngay trong lúc gõ, nên nếu ô bị ghi đè bằng bản cũ thì
 * chữ bị lặp kiểu "vvevetveti". Trong lúc bộ gõ đang ghép chữ (composition)
 * cũng chưa báo ra ngoài, đợi ghép xong mới lọc.
 */
export function OTim({ id, nhan, value, onChange, placeholder }) {
  const [chu, setChu] = useState(value || "");
  const dangGhep = useRef(false);
  const dangGo = useRef(false);
  // Chỉ nhận giá trị từ ngoài khi người dùng không gõ (ví dụ bấm "Xoá bộ lọc").
  useEffect(() => { if (!dangGo.current) setChu(value || ""); }, [value]);
  return (
    <div className="lhc-tim">
      <label className="sr-only" htmlFor={id}>{nhan}</label>
      <input
        id={id} type="search" value={chu} placeholder={placeholder}
        autoComplete="off" autoCorrect="off" spellCheck={false}
        onFocus={() => { dangGo.current = true; }}
        onBlur={() => { dangGo.current = false; }}
        onCompositionStart={() => { dangGhep.current = true; }}
        onCompositionEnd={(e) => { dangGhep.current = false; onChange(e.currentTarget.value); }}
        onChange={(e) => { setChu(e.target.value); if (!dangGhep.current) onChange(e.target.value); }}
      />
    </div>
  );
}

/** Dòng "Hiển thị N ..." kèm nút xoá lọc và chỗ cho công tắc kiểu xem. */
export function DongDem({ children, dangLoc, onXoa, phai }) {
  return (
    <div className="lhc-dong-dem">
      <p aria-live="polite">{children}</p>
      <div className="lhc-dong-dem-phai">
        {dangLoc && <button type="button" className="nut-chu" onClick={onXoa}>Xoá bộ lọc</button>}
        {phai}
      </div>
    </div>
  );
}
